import {
  ActionableNotification,
  DataTableSkeleton,
  InlineLoading,
  InlineNotification,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  Tag,
} from '@carbon/react';
import plur from 'plur';
import { domainLabels } from 'lib/evaluation-case/scenario-domains';
import { format0, formatNumber } from 'utils/number';
import {
  subtractCoverageLabel,
  subtractCoverageSuffix,
} from 'utils/subtract-coverage';
import { DrillInHeader } from './drill-in-header';
import { UncoveredError, useCaseUncovered } from './hooks';
import { EMPTY, linkCellRenderer } from './utils';
import type {
  UncoveredAssetRow,
  UncoveredCalculating,
  UncoveredResult,
  UncoveredSlotRow,
  UncoveredUnitRow,
} from './types';
import styles from './uncovered-drill-in.module.scss';

/**
 * Formats an acreage figure for the Assets / Units tables — commas, up to 2 dp,
 * the empty indicator when absent (mirrors the executive-summary treatment).
 *
 * @param {number | null} value - The acreage value, or null.
 * @return {string} The formatted acreage.
 */
const formatAcres = (value: number | null): string =>
  formatNumber({
    value,
    minPrecision: 0,
    maxPrecision: 2,
    includeCommas: true,
    fallback: EMPTY,
  });

/**
 * The Assets tab table — one row per uncovered CRM tract, with the BE-derived
 * columns (P4.md Additional Information; no Reason column — the BE emits none).
 * A Tract the package holds in part says how much of it the package holds.
 * Zero rows render as an empty body under the headers (the count tag reads 0).
 *
 * @param {object} props - The uncovered asset rows.
 * @param {UncoveredAssetRow[]} props.rows - The Assets-tab rows.
 * @return {JSX.Element} The read-only assets table.
 */
function AssetsTable({ rows }: { rows: UncoveredAssetRow[] }) {
  return (
    <div className={styles.tableWrapper}>
      <Table size="sm" aria-label="Uncovered assets">
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Account</TableHeader>
            <TableHeader>Instrument Type</TableHeader>
            <TableHeader>Net Acres</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const held = subtractCoverageLabel(
              row.includedSubtractCount,
              row.subtractCount
            );
            return (
              <TableRow key={row.href ?? row.name}>
                <TableCell>
                  {linkCellRenderer(row.name, row.href)}
                  {subtractCoverageSuffix(held)}
                </TableCell>
                <TableCell>
                  {linkCellRenderer(row.account, row.accountHref)}
                </TableCell>
                <TableCell>{row.instrumentType ?? EMPTY}</TableCell>
                <TableCell>{formatAcres(row.netAcres)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * The Units tab table — one row per uncovered development area.
 *
 * @param {object} props - The uncovered unit rows.
 * @param {UncoveredUnitRow[]} props.rows - The Units-tab rows.
 * @return {JSX.Element} The read-only units table.
 */
function UnitsTable({ rows }: { rows: UncoveredUnitRow[] }) {
  return (
    <div className={styles.tableWrapper}>
      <Table size="sm" aria-label="Uncovered units">
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Basin</TableHeader>
            <TableHeader>Type</TableHeader>
            <TableHeader>Acres</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.href}>
              <TableCell>{linkCellRenderer(row.name, row.href)}</TableCell>
              <TableCell>{row.basin ?? EMPTY}</TableCell>
              <TableCell>{row.type ?? EMPTY}</TableCell>
              <TableCell>{formatAcres(row.acres)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * The Slots tab table — one row per uncovered slot. The Slot cell links only
 * when the BE sends an `href` (absent for well-less slots).
 *
 * @param {object} props - The uncovered slot rows.
 * @param {UncoveredSlotRow[]} props.rows - The Slots-tab rows.
 * @return {JSX.Element} The read-only slots table.
 */
function SlotsTable({ rows }: { rows: UncoveredSlotRow[] }) {
  return (
    <div className={styles.tableWrapper}>
      <Table size="sm" aria-label="Uncovered slots">
        <TableHead>
          <TableRow>
            <TableHeader>Slot</TableHeader>
            <TableHeader>Area</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader>Formation</TableHeader>
            <TableHeader>Reason</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.slotId}>
              <TableCell>{linkCellRenderer(row.slotId, row.href)}</TableCell>
              <TableCell>{row.area ?? EMPTY}</TableCell>
              <TableCell>{row.status ?? EMPTY}</TableCell>
              <TableCell>{row.formation ?? EMPTY}</TableCell>
              <TableCell>{row.reason ?? EMPTY}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Whether a case-level uncovered response is still being computed (no rows yet).
 *
 * @param {UncoveredResult | undefined} data - The latest response, if any.
 * @return {boolean} true while the case's coverage is still being computed.
 */
function isCalculating(
  data: UncoveredResult | undefined
): data is UncoveredCalculating {
  return data !== undefined && data.status === 'calculating';
}

export interface UncoveredDrillInProps {
  /** The package the coverage is computed against. */
  packageId: number;
  /** The case whose gaps to show — the ACTIVE case, or a picker-previewed one. */
  caseId: number;
  /**
   * The case's display name (the header eyebrow) — `undefined` while the
   * main-origin case detail is still loading, so the eyebrow row doesn't
   * render as an empty span that pops in (a preview always carries a name).
   */
  caseName: string | undefined;
  /** Picker-preview mode — a "Preview" tag + conditional subtitle; nothing commits. */
  isPreview: boolean;
  /** Return to the previous view (the section routes main vs picker by origin). */
  onBack: () => void;
}

/**
 * The Uncovered drill-in (P4 FR#6): what the case does NOT cover of the
 * package — contained Tabs (Assets / Units / Slots, each with a count tag from
 * `counts`) over small read-only tables with the BE-derived columns (no Reason
 * column — the BE emits none). Reached committed (the coverage tile) or as a
 * picker PREVIEW (a "Preview" tag beside the eyebrow + "if this case were
 * selected" subtitle phrasing — the selection is NOT committed); Back routes to
 * main or the picker accordingly (the section owns that, by origin). The
 * subtitle waits for `counts` (undefined while loading).
 *
 * @param {UncoveredDrillInProps} props - The case/package identity, preview flag, and back handler.
 * @return {JSX.Element} The uncovered drill-in surface.
 */
export function UncoveredDrillIn({
  packageId,
  caseId,
  caseName,
  isPreview,
  onBack,
}: UncoveredDrillInProps) {
  const {
    data: result,
    error,
    isPending,
    isError,
    refetch,
  } = useCaseUncovered(caseId, packageId);
  // A failed poll keeps the last 'calculating' body as `result`, and polling stops: once the query
  // is in error, the error says what happened, not a calculating line that never moves.
  const calculating = !isError && isCalculating(result) ? result : undefined;
  const data = isCalculating(result) ? undefined : result;
  // A case-level query_timeout is deterministic for the package: no Retry.
  const notRetryable = error instanceof UncoveredError && !error.retryable;

  // `counts.total` spans all three kinds, so the noun is the neutral "item"
  // (the mock's "assets" phrasing fit its assets-only count).
  const total = data?.counts.total;
  const subtitle =
    total !== undefined
      ? `${format0(total)} ${plur('item', total)} ${
          isPreview
            ? 'uncovered if this case were selected'
            : 'not evaluated by this case'
        } · An assigned scenario's coverage misses them`
      : undefined;

  return (
    <div className={styles.uncovered}>
      <DrillInHeader
        eyebrow={caseName}
        eyebrowTag={
          isPreview ? (
            <Tag
              size="sm"
              type="cool-gray"
              aria-label="Preview only — the selection is not committed"
            >
              Preview
            </Tag>
          ) : undefined
        }
        title="Uncovered assets"
        subtitle={subtitle}
        onBack={onBack}
      />

      {isPending && (
        <DataTableSkeleton
          columnCount={4}
          rowCount={5}
          showHeader={false}
          showToolbar={false}
        />
      )}

      {calculating && (
        <InlineLoading
          className={styles.banner}
          description={`Calculating coverage for ${domainLabels(
            calculating.pendingDomains
          )}…`}
        />
      )}

      {isError && notRetryable && (
        <InlineNotification
          className={styles.banner}
          role="alert"
          kind="error"
          lowContrast
          hideCloseButton
          title="Couldn't load the uncovered assets"
          subtitle={`${error.message}. Retrying won't help.`}
        />
      )}

      {isError && !notRetryable && (
        <ActionableNotification
          className={styles.banner}
          inline
          kind="error"
          // hasFocus off: its auto-focus scrolls the errored section into view (page jump)
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load the uncovered assets"
          subtitle={
            // The envelope's own message (a 503's "try again in a few minutes").
            error instanceof Error
              ? error.message
              : 'The coverage detail is unavailable right now.'
          }
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetch()}
        />
      )}

      {data && (
        <Tabs>
          <TabList aria-label="Uncovered asset types" contained>
            <Tab>
              Assets{' '}
              <Tag className={styles.countTag} size="sm">
                {format0(data.counts.assets)}
              </Tag>
            </Tab>
            <Tab>
              Units{' '}
              <Tag className={styles.countTag} size="sm">
                {format0(data.counts.units)}
              </Tag>
            </Tab>
            <Tab>
              Slots{' '}
              <Tag className={styles.countTag} size="sm">
                {format0(data.counts.slots)}
              </Tag>
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel className={styles.tabPanel}>
              <AssetsTable rows={data.assets} />
            </TabPanel>
            <TabPanel className={styles.tabPanel}>
              <UnitsTable rows={data.units} />
            </TabPanel>
            <TabPanel className={styles.tabPanel}>
              <SlotsTable rows={data.slots} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </div>
  );
}
